import { RESEARCH_ELECTRONIC_SIGNATURES_ERRORS, RESEARCH_PROJECT_MEMBERS_APPROVAL_ACTIONS } from '@/config/research';
import Vue from 'vue';

export const ProjectMembers = {
  namespaced: true,

  state: {
    updatingProjectMember: false,
    userCanManageRoles: false,
    userCanAddMembers: false,
    pagination: {},
    eSignError: false,
    loading: false,
    saving: false,
    approving: false,
    members: {},
  },

  getters: {
    getProjectUsers: (state, getters) => (uuid) => getters.getProjectMembers(uuid).map(({ user }) => user),

    areMembersLoaded: (state) => (uuid) => Object.keys(state.members).includes(uuid),

    isUserAllowedToCreate: ({ userCanAddMembers }) => userCanAddMembers,

    getProjectMembers: (state) => (uuid) => state.members[uuid] ?? [],

    areMembersLoading: (state) => state.loading,
    // TODO: review if used getMembersSaving
    getMembersSaving: ({ saving }) => saving,

    getPaginationData:
      ({ pagination }) =>
      (uuid) =>
        pagination?.[uuid] ?? {},

    updatingProjectMember: ({ updatingProjectMember }) => updatingProjectMember,

    isApprovingMember: ({ approving }) => approving,
  },

  mutations: {
    setLoading(state, loading) {
      state.loading = loading;
    },

    setSaving(state, saving) {
      state.saving = saving;
    },

    setPagination(state, pagination) {
      state.pagination = { ...state.pagination, ...pagination };
    },

    updateProjectMembers(state, newMembers) {
      Vue.set(state, 'members', { ...state.members, ...newMembers });
    },

    deleteProjectMembers(state, project) {
      Vue.delete(state.members, project);
    },

    setProjectClean(state, uuid) {
      state.dirty = state.dirty.filter((item) => item !== uuid);
    },

    setAddPermission(state, allowed) {
      state.userCanAddMembers = allowed;
    },

    setRolePermission(state, allowed) {
      state.userCanManageRoles = allowed;
    },

    deleteMember(state, { project, member }) {
      const members = state.members[project];
      Vue.delete(
        members,
        members.findIndex((m) => m.uuid === member)
      );
    },

    setUpdatingProjectMember(state, bool) {
      Vue.set(state, 'updatingProjectMember', bool);
    },

    setApprovingMember(state, bool) {
      state.approving = bool;
    },

    setESignError(state, error) {
      state.eSignError = error;
    },
  },

  actions: {
    async loadProjectMembers({ commit }, { project, repository, filter, page, perPage, sortBy, sortDesc, uuids }) {
      if (!project || !repository) {
        return;
      }

      let members = {};
      let pagination = {};

      commit('deleteProjectMembers', project);
      commit('setLoading', true);
      try {
        const {
          data: { data, meta, links, can },
        } = await this._vm.$api('repositories.projects.members.index', {
          version: '1.0',
          repository,
          sortDesc,
          perPage,
          project,
          filter,
          sortBy,
          uuids,
          page,
        });

        members = data;
        pagination = { ...meta, ...links };

        commit('setAddPermission', can?.add);
        commit('setRolePermission', can?.editRoles);
      } catch (err) {
        console.error(err);
      }

      commit('updateProjectMembers', { [project]: members });
      commit('setPagination', { [project]: pagination });

      commit('setLoading', false);
    },

    async updateMember({ commit }, { project, repository, member, newData, comment }) {
      const { siteUuids, roleId } = newData;
      commit('setUpdatingProjectMember', true);

      try {
        await this._vm.$api(
          'repositories.projects.members.update',
          {
            version: '1.0',
            repository,
            project,
            member,
          },
          {
            sites: siteUuids,
            role_id: roleId,
            comment,
          }
        );
      } catch (err) {
        console.error(err);
      }
      commit('setUpdatingProjectMember', false);
    },

    deleteMember({ commit }, { project, repository, member, comment }) {
      commit('deleteMember', { project, member });
      this._vm.$api(
        'repositories.projects.members.destroy',
        {
          version: '1.0',
          repository,
          project,
          member,
        },
        {
          comment,
        }
      );
    },

    async memberApprovalHandler({ commit }, { project, repository, member, comment, action, eSign }) {
      commit('setApprovingMember', true);
      commit('setESignError', null);

      if (!RESEARCH_PROJECT_MEMBERS_APPROVAL_ACTIONS[action]) throw new Error('Invalid action');

      try {
        const response = await this._vm.$api(
          'repositories.projects.members.e-sign',
          {
            version: '1.0',
            repository,
            project,
            member,
          },
          {
            comment,
            action,
            ...(eSign ? { 'e-sign': eSign } : {}),
          }
        );

        if (response?.status !== 200) {
          throw new Error();
        }
      } catch (error) {
        if (error.message === RESEARCH_ELECTRONIC_SIGNATURES_ERRORS.INVALID_SIGNATURE) {
          commit('setESignError', RESEARCH_ELECTRONIC_SIGNATURES_ERRORS.INVALID_SIGNATURE);
        } else {
          commit('setESignError', RESEARCH_ELECTRONIC_SIGNATURES_ERRORS.GENERIC_ERROR);
        }
      } finally {
        commit('setApprovingMember', false);
      }
    },

    resetProjectMembers({ commit }, projectUuid) {
      commit('deleteProjectMembers', projectUuid);
    },

    resetESignatureError({ commit }) {
      commit('setESignError', null);
    },
  },
};
